import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Check, Info, Pencil, Sparkles, Wand2, X } from "lucide-react";

import { api, track } from "@/lib/api";
import { labelFor, typeFromPattern } from "@/lib/options";
import { StyleRowCard } from "@/components/cards";
import { Button, Card, ConfidenceBar, IconCircle, ScreenHeader } from "@/components/kit";
import { Page } from "@/components/layout";
import { ProfileEditor } from "@/components/widgets";
import { useApp } from "@/store";

const AI_FIELDS = ["face_shape", "hair_pattern", "length", "density"];
const DISCLAIMER = "AI styling suggestions are estimates, not professional or medical assessments.";
const LABEL = { face_shape: "Face shape", hair_pattern: "Hair pattern", length: "Length", density: "Apparent density" };

function EstimateCell({ label, value, est, testId, edited, wide, small }) {
  return (
    <div data-testid={testId} className={`flex flex-col gap-1.5 rounded-xl border border-line bg-white p-3 ${wide ? "col-span-2" : ""}`}>
      <span className="t-label">{label}</span>
      <span data-testid={`${testId}-value`} className={small ? "t-body" : "t-title"}>{value}</span>
      {edited ? <span className="t-caption">Edited by you</span> : est?.value ? <ConfidenceBar testId={`${testId}-confidence`} value={est.confidence} /> : <span className="t-caption">Pick it yourself under Correct my results.</span>}
    </div>
  );
}

export default function ScanResults() {
  const { scanDraft, profile, saveProfile, setScanDraft, scanPhoto, toast, rememberScan } = useApp();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => {
    if (!scanDraft) return profile;
    const next = { ...profile, confidences: {} };
    AI_FIELDS.forEach((k) => {
      const e = scanDraft[k];
      if (e?.value) {
        next[k] = e.value;
        next.confidences[k] = e.confidence;
      }
    });
    if (next.hair_pattern) next.hair_type = typeFromPattern(next.hair_pattern) ?? next.hair_type;
    next.strand_notes = scanDraft.texture?.notes || scanDraft.strand_notes || null;
    next.visible_hairstyle = scanDraft.visible_hairstyle?.value || null;
    return next;
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [top, setTop] = useState(null);

  if (!scanDraft && !top) return <Navigate to="/scan" replace />;

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...draft, source: "scan", scanned_at: new Date().toISOString() };
      await saveProfile(p);
      const r = await api("/recommendations", { method: "POST", body: { profile: p } });
      setTop(r.recommendations);
      track("recommendations_viewed", { source: "scan" });
      toast(rememberScan ? "Hair profile saved." : "Using these results for this session (not saved).", "success");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };
  const done = () => {
    setScanDraft(null);
    navigate("/home", { replace: true });
  };
  const valueText = (k, v) => {
    if (!v) return "Couldn't estimate";
    if (k === "hair_pattern") return `${v} · ${labelFor("hair_type", typeFromPattern(v))}`;
    return labelFor(k, v);
  };

  if (top) {
    return (
      <div data-testid="scan-top5-screen">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 pb-2 pt-4 md:px-6">
          <div className="w-11" />
          <span className="t-label">{rememberScan ? "Profile saved" : "This session only · not saved"}</span>
          <IconCircle testId="scan-top5-close" icon={X} label="Close" onClick={done} />
        </div>
        <Page className="flex flex-col gap-4 pb-32">
          <h1 data-testid="scan-top5-title" className="t-h1">YOUR TOP 5 FROM THIS SCAN.</h1>
          <p className="t-body">
            Matched to {[valueText("hair_pattern", draft.hair_pattern), labelFor("length", draft.length), draft.face_shape ? `${labelFor("face_shape", draft.face_shape)} face` : null].filter((x) => x && x !== "—" && x !== "Couldn't estimate").join(" · ")}
            {draft.hold || draft.finish || draft.preferred_style ? " plus your styling preferences." : "."} Tap a style for its BREEZ match and tutorial.
          </p>
          {top.map((r, i) => (
            <div key={r.style.slug} className="animate-fade-up flex flex-col gap-2" style={{ animationDelay: `${i * 80}ms` }}>
              <StyleRowCard testId={`scan-top5-${i + 1}`} rank={i + 1} style={r.style} explanation={r.explanation} faceNote={r.face_note} onClick={() => navigate(`/style/${r.style.slug}`)} />
              {scanPhoto ? <Button testId={`scan-top5-tryon-${i + 1}`} title="Try this hairstyle" icon={Wand2} variant="outline" onClick={() => navigate(`/try-on?slug=${r.style.slug}&fromScan=1`)} /> : null}
            </div>
          ))}
          {scanPhoto ? <p data-testid="scan-top5-tryon-note" className="t-caption">Try-on reuses your scan selfie from this browser&apos;s memory. AI-generated preview — actual results may vary.</p> : null}
          <p className="t-caption">{DISCLAIMER}</p>
        </Page>
        <Footer><Button testId="scan-top5-done" title="Done" onClick={done} className="w-full" /></Footer>
      </div>
    );
  }

  const s = scanDraft;
  const tips = s.styling_suggestions ?? [];
  return (
    <div data-testid="scan-results-screen">
      <ScreenHeader testId="scan-results-back" label="Scan results" fallback="/scan" />
      <Page className="flex flex-col gap-6 pb-32">
        <div className="flex flex-col gap-2">
          <h1 data-testid="scan-results-title" className="t-h1">YOUR BREEZ HAIR PROFILE</h1>
          <p className="t-body">Here&apos;s what the AI estimated from your photo. Confidence shows how sure it is — lower usually means lighting, angle or covered hair made it harder to tell. Use it as a starting point: you know your hair best.</p>
          <div data-testid="scan-disclaimer" className="mt-2 flex items-center gap-2 rounded-xl bg-tert p-3">
            <Info className="h-[18px] w-[18px] shrink-0 text-navy" aria-hidden />
            <p className="t-caption flex-1 !text-navy">{DISCLAIMER}</p>
          </div>
          {s.image_quality !== "good" && s.quality_tip ? <p data-testid="scan-quality-tip" className="t-caption !text-[#B45309]">Photo quality: {s.image_quality}. {s.quality_tip}</p> : null}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {AI_FIELDS.map((k) => (
            <EstimateCell key={k} testId={`scan-result-${k}`} label={LABEL[k]} value={valueText(k, draft[k])} est={s[k]} edited={!!s[k]?.value && draft[k] !== s[k].value} />
          ))}
          <EstimateCell testId="scan-result-visible_hairstyle" label="Current hairstyle" value={s.visible_hairstyle?.value || "Couldn't estimate"} est={s.visible_hairstyle} wide />
          <EstimateCell testId="scan-result-texture" label="Texture notes" value={s.texture?.notes || s.strand_notes || "Couldn't estimate"} est={{ value: s.texture?.notes ?? null, confidence: s.texture?.confidence ?? 0 }} wide small />
        </div>
        <Button testId="scan-correct-results" title={editing ? "Done correcting" : "Correct my results"} icon={editing ? Check : Pencil} variant="outline" onClick={() => setEditing((e) => !e)} />
        {editing ? (
          <ProfileEditor profile={draft} onChange={setDraft} fields={["face_shape", "hair_pattern", "length", "density", "thickness"]} estimates={{ face_shape: s.face_shape, hair_pattern: s.hair_pattern, length: s.length, density: s.density }} />
        ) : null}
        {tips.length ? (
          <Card testId="scan-suggestions">
            <p className="t-label">Styling suggestions for your hair</p>
            <ul className="mt-2 space-y-2">
              {tips.map((t, i) => (
                <li key={i} className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-ocean" aria-hidden /><span data-testid={`scan-suggestion-${i}`} className="t-body flex-1">{t}</span></li>
              ))}
            </ul>
          </Card>
        ) : null}
        <p className="t-caption">Thickness is best judged by you — the camera can&apos;t feel your strands. Your photo was not stored on our server.</p>
      </Page>
      <Footer><Button testId="scan-results-save" title="Save & see my top 5" icon={Sparkles} loading={saving} onClick={save} className="w-full" /></Footer>
    </div>
  );
}

export function Footer({ children }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-divider bg-off/95 py-3 backdrop-blur">
      <div className="mx-auto flex w-full max-w-3xl gap-3 px-5 md:px-6">{children}</div>
    </div>
  );
}
